'use client';
import BrandLogo from '@/views/commonComponent/brand/BrandLogo';
import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Container, Nav, Navbar, Row, Col } from 'react-bootstrap';
import { AiOutlineMenu } from 'react-icons/ai';
import './style.scss';
import { Avatar, Dropdown, MenuProps } from 'antd';
import SignOut from '../sign-out/SignOut';
import TosModal from '../tos-modal/TosModal';
import { appAction, appState } from '@/store/store';

function Header() {
  const pathname = usePathname();
  const [scroll, setScroll] = useState(false);
  // Subscribe to the live store value so login/logout updates the header
  // without a full page reload (the pre-PR localStorage read at first render
  // never re-ran, leaving stale state). The shape change: localStorage stored
  // `{ user: ... }`, so existing reads were `user.user.<field>`; userObj is the
  // user object directly, so the `.user` indirection is removed throughout.
  const userObj = appState((state) => state.AuthModel.userObj);
  // ...but `userObj` starts as `undefined` until getUserObj resolves.
  // For a logged-in user hard-refreshing a public page, that means a brief
  // flash of the "Login" button before the /user request completes. Seed
  // from the localStorage cache (one-shot at mount) so the first paint is
  // already correct. Once getUserObj fires, `userObj` takes over via
  // the nullish-coalescing fallback.
  const [cachedUser] = useState<typeof userObj>(() => {
    if (typeof window === 'undefined') return undefined;
    try {
      const raw = window.localStorage.getItem('user');
      return raw ? JSON.parse(raw)?.user : undefined;
    } catch {
      return undefined;
    }
  });
  // After the first auth check resolves we trust only the store -- if the
  // cached user existed but Firebase says otherwise, getUserObj clears
  // localStorage and never calls setUserObj, so without this gate the
  // cached fallback would keep rendering the stale avatar forever.
  const [authChecked, setAuthChecked] = useState(false);
  const user = authChecked ? userObj : (userObj ?? cachedUser);
  const [signupModal, setSignupModal] = useState(false);
  const router = useRouter();
  const [activeId, setActiveId] = useState('');
  const getUserObj = appAction((action) => action.AuthModel.getUserObj);

  const handleMenuControlButton = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    let sidebarFixed = document.querySelector('.menu');
    sidebarFixed && sidebarFixed.classList.toggle('show');
  };

  const handleMenuController = (e: any) => {
    if (e.target.href) {
      var sidebarFixed = document.querySelector('.menu');
      sidebarFixed && sidebarFixed.classList.remove('show');
    }
  };

  // Memoize the base64 encoding so re-renders triggered by unrelated state
  // (scroll, activeId, modal toggles) don't re-run Buffer.from on the profile
  // picture bytes. Recomputes only when the relevant profilePic.data changes.
  const profileImg = useMemo(() => {
    if (user?.type === 'advisor') {
      if (user?.advisor?.profilePic?.data?.length) {
        return `data:image/jpeg;base64,${Buffer.from(user.advisor.profilePic.data).toString('base64')}`;
      }
      return '/noUser.png';
    } else if (user?.type === 'provider') {
      if (user?.provider?.profilePic?.data?.length) {
        return `data:image/jpeg;base64,${Buffer.from(user.provider.profilePic.data).toString('base64')}`;
      }
      return '/noUser.png';
    }
    return '/noUser.png';
  }, [user?.type, user?.advisor?.profilePic?.data, user?.provider?.profilePic?.data]);

  const handleProfileNavigate = () => {
    user?.advisor
      ? router.push('/profile/advisor-my-profile')
      : user?.provider
        ? router.push('/profile/provider-my-profile')
        : router.push('/dashboard');
  };

  const items: MenuProps['items'] = [
    {
      key: 'description',
      label: (
        <div className="description">
          {' '}
          <Avatar style={{ height: '45px', width: '45px' }} src={profileImg} />{' '}
          <div>
            {' '}
            {user?.firstName} <span> {user?.emailId} </span>{' '}
          </div>{' '}
        </div>
      ),
    },
    {
      key: 'MyProfile',
      label: (
        <label
          style={{ width: '100%', cursor: 'pointer', fontWeight: '500' }}
          onClick={() => {
            user?.requestStatus?.status === 'incomplete'
              ? window.alert('Complete account setup first')
              : router.push('/profile/advisor-my-profile');
          }}
        >
          {' '}
          My Profile{' '}
        </label>
      ),
    },
    {
      key: 'Sign out',
      label: (
        <label
          style={{ width: '100%', cursor: 'pointer', fontWeight: '500' }}
          onClick={() => {
            setSignupModal(true);
          }}
        >
          {' '}
          Sign Out{' '}
        </label>
      ),
    },
  ];

  const mobViewOptions: MenuProps['items'] = [
    {
      key: 'description',
      label: (
        <div
          className="description"
          onClick={() => {
            user ? null : router.push('/getSupport');
          }}
        >
          {' '}
          <Avatar style={{ height: '45px', width: '45px' }} src={profileImg} />{' '}
          <div>
            {' '}
            {user ? user?.firstName : `Sign Up`} <span> {user ? user?.emailId : ''} </span>{' '}
          </div>{' '}
        </div>
      ),
    },
    {
      key: 'About Us',
      label: (
        <label
          style={{ width: '100%', cursor: 'pointer', fontWeight: '500' }}
          onClick={() => {
            router.push('/about');
          }}
        >
          {' '}
          About Us{' '}
        </label>
      ),
    },
    {
      key: 'Services',
      label: (
        <label style={{ width: '100%', cursor: 'pointer', fontWeight: '500' }}>
          {' '}
          <Link href="/#services" style={{ textDecoration: 'none', color: '#818181' }}>
            Services
          </Link>{' '}
        </label>
      ),
    },
    {
      key: 'Start Now',
      label: (
        <label
          style={{ width: '100%', cursor: 'pointer', fontWeight: '500' }}
          onClick={() => {
            router.push('/getSupport');
          }}
        >
          {' '}
          Start Now{' '}
        </label>
      ),
    },
    {
      key: 'Contact Us',
      label: (
        <label
          style={{ width: '100%', cursor: 'pointer', fontWeight: '500' }}
          onClick={() => {
            router.push('/contact');
          }}
        >
          {' '}
          Contact Us{' '}
        </label>
      ),
    },
    {
      key: 'Sign out',
      label: (
        <label
          style={{ width: '100%', cursor: 'pointer', fontWeight: '500' }}
          onClick={() => {
            user ? setSignupModal(true) : router.push('/auth/login');
          }}
        >
          {user ? `Sign Out` : `Login`}
        </label>
      ),
    },
  ];
  const handleSignupModal = () => {
    setSignupModal(false);
  };

  useEffect(() => {
    // Resolve the auth check (success or failure) and then flip the gate.
    // setUserObj is dispatched on success; on failure we still need to flip
    // the gate so the cached fallback stops rendering stale data.
    Promise.resolve(getUserObj()).finally(() => setAuthChecked(true));

    // Single scroll handler: pre-fix there were TWO addEventListener
    // calls -- one anonymous (which couldn't be removed) and one named
    // (handleScroll). Only handleScroll was cleaned up, so the anonymous
    // one leaked on every remount. Merged the body of the anonymous
    // listener (`setScroll(window.scrollY > 200)`) into handleScroll so
    // a single registration / cleanup pair is enough.
    const handleScroll = () => {
      setScroll(window.scrollY > 200);

      const sections = document.querySelectorAll('section[id]');
      let currentId: any = '';

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
          currentId = section.getAttribute('id');
        }
      });

      setActiveId(currentId);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // mount

    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: auth check + scroll listener registration. Adding `getUserObj` would re-fire the auth thunk on every render.
  }, []);

  return (
    <>
      <header className={`main-header-container ${scroll ? 'header-fixed' : ''}`}>
        <Container>
          <Row className="mainDiv">
            <Col lg={9}>
              <div className="leftNavbar">
                <div className="logo">
                  <Link href="https://roffice.team/">
                    <BrandLogo tone="dark" size="md" />
                  </Link>
                </div>
                <Dropdown menu={{ items: mobViewOptions }} trigger={['click']}>
                  <button
                    className="header-toggler d-block d-lg-none"
                    // onClick={handleMenuControlButton}
                  >
                    <AiOutlineMenu />
                  </button>
                </Dropdown>
                <div className="menu">
                  {/* <ul onClick={handleMenuController}>
                                        <li className={`${pathname == "/about" ? "active" : ""}`}><Link href="/about">About Us</Link></li>
                                        <li className={`${activeId === "services" ? "active" : ""}`}><Link href="/#services">Services</Link></li>
                                        <li className={`${pathname == "/getSupport" ? "active" : ""}`}><Link href="/getSupport">Start Now</Link></li>
                                        <li className={`${pathname == "/contact" ? "active" : ""}`}><Link href="/contact">Contact Us</Link></li>
                                        <li className="d-block d-lg-none"><Link href="/auth/login">Login</Link></li>
                                        <li className="d-block d-lg-none"><Link href="/getSupport">Sign Up</Link></li>
                                    </ul> */}
                </div>
              </div>
            </Col>
            <Col lg={3} className="d-none d-lg-block">
              {user?.type ? (
                <div className="rightNavbar">
                  <Avatar
                    className="user-profile"
                    src={profileImg}
                    onClick={handleProfileNavigate}
                  />
                  <Dropdown menu={{ items }} trigger={['click']}>
                    <img
                      alt=""
                      style={{ cursor: 'pointer' }}
                      className="quick-access"
                      src="/header/dropDown.svg"
                    />
                  </Dropdown>
                </div>
              ) : (
                <div className="rightNavbar">
                  <ul>
                    <li className="nav-item">
                      <Link href="/auth/login">Login</Link>
                    </li>
                  </ul>
                </div>
              )}
            </Col>
          </Row>
          <SignOut
            signUpModalOpen={signupModal}
            handleSignUpModal={handleSignupModal}
            type="advisor"
          />
          {/* TosModal renders itself only when userObj.tosAcceptedAt is null
              for advisor/provider users. Mounted here in the global Header
              (which the root layout pulls in on every page) so the re-prompt
              fires immediately after getUserObj resolves. */}
          <TosModal />
        </Container>
      </header>
    </>
  );
}
export default Header;
